import { PureComponent } from 'react';
import PropTypes from 'prop-types';

import withBreakpoint from 'core/hocs/withBreakpoint';
import withRequestId from 'core/hocs/withRequestId';

import { isInViewport } from 'core/utils/dom';
import breakpointPropTypes from 'core/utils/prop-types/breakpoint';

const visibleRatio = {
  desktop: 0.5,
  mobile: 0.3,
};


/**
 * @author Pavel Povalyaev <p.povalyaev@rambler-co.ru>
 */

class BaseSender extends PureComponent {
  static propTypes = {
    /**
     * Параметр идентифицирующий контекст вызова в рамках страницы.
     *
     * @ignore
     */
    requestId: PropTypes.string,

    /** Коллбек который рассылает события показа*/
    onShow: PropTypes.func,

    /** Коллбек который рассылает события клика*/
    onClick: PropTypes.func,

    /**
     * checkScrollY = true делает так, что обзервер не будет инициализироваться если компонент попадет в viewport когда scrollY=0,
     * это нужно для компонентов, которые находятся после большого контента и пока контент находится в состоянии загрузки они могут
     * подскочить вверх и произойдет ложное срабатывание
     */
    checkScrollY: PropTypes.bool,

    /**
     * По умолчанию событие отправляется только если клик сделан
     * по ссылке (`<a>`). Этот проп позволяет отключить проверку и
     * отправлять событие при клике по любому элементу.
     */
    skipTargetCheck: PropTypes.bool,

    /**
     * Отключить обёртку.
     * Можно использовать, например, для включения обработки событий
     * только на определённых брейкпойнтах.
     */
    disabled: PropTypes.bool,

    /** @ignore */
    breakpoint: breakpointPropTypes(),

    /**
     * BaseSender оборачивает `children` в `div`.<br/>
     * Можно задать класс для этой обертки, что бывает полезно при верстке.
     */
    className: PropTypes.string,
  };

  componentDidMount() {
    if (!this.props.disabled && this.wrapper && this.props.onShow) {
      this.initObserver();
      window.addEventListener('beforeunload', this.sendShowOnUnload);
    }
  }

  componentDidUpdate(prevProps) {
    if (this.observer && !this.observing && prevProps.requestId !== this.props.requestId) {
      this.observing = true;
    }
  }

  componentWillUnmount() {
    this.sendShowOnUnload();
    window.removeEventListener('beforeunload', this.sendShowOnUnload);
    this.unobserve();
  }

  /** Используем this.observing для управления повторным вызовом onShow, в т.ч. в случаях,
   * когда при клиентской навигации компонент не перемонтируется, а перерендеривается.
   * */

  observing = false;
  intersected = false;

  initObserver = () => {
    this.observer = new IntersectionObserver((entries) => {
      // Не навешиваем обработчик события на объекты, от которых мы ожидаем, что они стоят после больших блоков и находясь в состоянии загрузки
      // могут подбросить объект во вьюпорт и вызвать ложное срабатывание в самом начале просмотра страницы когда scrollY = 0
      if (this.observing && (!this.props.checkScrollY || window.scrollY !== 0) && entries[0].intersectionRatio > 0) {
        this.props.onShow();
        this.observing = false;
      }
      if (entries[0].intersectionRatio > 0) {
        this.intersected = true;
      }
    }, {
      threshold: visibleRatio[this.props.breakpoint] || 0.5,
    });
    this.observer.observe(this.wrapper);
    this.observing = true;
  };

  unobserve = () => {
    if (this.observer) this.observer.disconnect();
  };

  sendShowOnUnload = () => {
    /** Необходимо, чтобы отправлять события при уходе со страницы
     * (beforeunload) без скролла и наличии блока в самом верху,
     * так как в observer не отправляет события при scrollY = 0 */
    if (this.observer && this.observing && isInViewport(this.wrapper, visibleRatio.desktop) && this.intersected) {
      this.props.onShow();
      this.observing = false;
    }
  };

  handleClick = e => {
    const targetApproved = this.props.skipTargetCheck || e.target.closest('a');
    if (this.props.onClick && targetApproved) {
      this.props.onClick();
    }
  };

  render() {
    const {
      children,
      disabled,
      className,
    } = this.props;

    if (disabled) return children;

    return (
      <div
        onClick={this.handleClick}
        ref={node => {
          this.wrapper = node;
        }}
        className={className}
      >
        {children}
      </div>
    );
  }
}

const BaseSenderWithHOCs = withRequestId(withBreakpoint(BaseSender));

BaseSenderWithHOCs.displayName = 'BaseSender';

export { BaseSender as StorybookComponent };
export default BaseSenderWithHOCs;
